import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import CoachAnalyticsTile from "../components/CoachAnalyticsTile";
import CoachStudentsSection from "../components/CoachStudentsSection";
import CoachBroadcastSection from "../components/CoachBroadcastSection";
import StripeConnectCard from "../components/StripeConnectCard";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import DateTimePicker from "../components/DateTimePicker";
import FacilityPicker from "../components/FacilityPicker";

export default function CoachDashboard() {
  const { t } = useI18n();
  const { user, refreshMe } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sports, setSports] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({
    sport_id: "", facility_id: null, location: "", title: "", description: "", lesson_type: "private",
    visibility: "public", max_players: 1, price: 30, currency: "EUR",
    start_time: "", end_time: "", all_day: false,
  });
  const fetchAll = useCallback(async () => {
    const [{ data: sp }, { data: ls }, { data: inv }] = await Promise.all([
      api.get("/sports"), api.get("/lessons"), api.get("/invoices"),
    ]);
    setSports(sp);
    setLessons(ls.filter((l) => l.coach_id === user.id));
    setInvoices(inv.filter((i) => i.issued_by === user.id));
    setForm((f) => (f.sport_id || !sp[0] ? f : { ...f, sport_id: sp[0].id }));
  }, [user.id]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Handle the Stripe Connect return/refresh callback. Refresh the user record
  // so charges_enabled flips immediately if the user finished onboarding,
  // then strip the query param so the toast doesn't fire on a manual refresh.
  useEffect(() => {
    const flag = searchParams.get("stripe_connect");
    if (!flag) return;
    (async () => {
      try {
        const { data } = await api.get("/payments/connect/status");
        await refreshMe?.();
        if (data.charges_enabled) toast.success("Stripe Connect ready — you can now receive payments.");
        else if (flag === "return") toast.message("Stripe Connect saved. Finish remaining steps to receive payouts.");
      } catch (statusErr) {
        // Status fetch is informational — failure here shouldn't block the
        // URL cleanup or surface an error to the user (they just landed back
        // from Stripe and don't care about a status fetch hiccup).
        // eslint-disable-next-line no-console
        console.warn("[stripe-connect] status fetch on callback failed:", statusErr?.message || statusErr);
      }
      const next = new URLSearchParams(searchParams);
      next.delete("stripe_connect");
      setSearchParams(next, { replace: true });
    })();
  }, [searchParams, setSearchParams, refreshMe]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.sport_id) { toast.error("Pick a sport first"); return; }
    if (!form.start_time) { toast.error("Pick a start date"); return; }
    try {
      await api.post("/lessons", {
        ...form,
        max_players: Number(form.max_players),
        price: Number(form.price),
        start_time: new Date(form.start_time).toISOString(),
        end_time: form.end_time ? new Date(form.end_time).toISOString() : null,
      });
      toast.success("Lesson created");
      fetchAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  return (
    <AppLayout>
      <h1 className="font-heading text-3xl sm:text-4xl font-black">Coach {t("dashboard")}</h1>
      <p className="text-zinc-400 mt-2">Schedule lessons, send invoices, grow your roster.</p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="coach-kpis">
        <Kpi label="Lessons" value={lessons.length} />
        <Kpi label="Invoices" value={invoices.length} />
        <Kpi label={t("paid")} value={invoices.filter((i) => i.status === "paid").length} />
        <Kpi label={t("revenue")} value={`${invoices.filter((i) => i.status === "paid").reduce((a, i) => a + (i.amount || 0), 0)} EUR`} />
      </div>

      <div className="mt-6">
        <CoachAnalyticsTile windowHours={48} kind="lesson" />
      </div>

      <CoachStudentsSection />
      <CoachBroadcastSection />

      {!user?.stripe_account_id && (
        <div className="mt-4 pp-card p-4 flex items-center justify-between flex-wrap gap-3" data-testid="coach-stripe-connect">
          <div>
            <div className="font-bold">Connect payouts</div>
            <div className="text-xs text-zinc-500">Enable Stripe to receive payments minus 5% platform fee.</div>
          </div>
          <button onClick={async () => {
            try {
              const { data } = await api.post("/payments/connect/onboard", { origin_url: window.location.origin });
              window.open(data.onboarding_url, "_blank", "noopener,noreferrer");
              toast.message("Stripe Connect opened in a new tab — finish onboarding there.");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
          }} data-testid="coach-connect-btn" className="pp-btn-primary px-4 py-2 rounded-lg text-sm">Connect with Stripe</button>
        </div>
      )}
      {user?.stripe_account_id && <StripeConnectCard />}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="pp-card-elevated p-6 space-y-3" data-testid="coach-create-lesson">
          <h3 className="font-heading text-xl font-bold">Create Lesson</h3>
          <div className="grid grid-cols-2 gap-3">
            <select className="pp-input" data-testid="cl-sport" value={form.sport_id} onChange={set("sport_id")}>
              <option value="" disabled>Choose a sport…</option>
              {sports.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select className="pp-input" data-testid="cl-type" value={form.lesson_type} onChange={set("lesson_type")}><option>private</option><option>group</option><option>clinic</option></select>
          </div>
          <input className="pp-input" data-testid="cl-title" required value={form.title} onChange={set("title")} placeholder={t("title")} />
          <textarea className="pp-input" data-testid="cl-desc" rows={2} value={form.description} onChange={set("description")} placeholder={t("description")} />
          <FacilityPicker
            sportSlug={(sports.find((s) => s.id === form.sport_id) || {}).slug}
            value={{ facility_id: form.facility_id, location: form.location }}
            onChange={({ facility_id, location }) => setForm((f) => ({ ...f, facility_id, location }))}
            testIdPrefix="cl-facility"
          />
          <div className="grid grid-cols-2 gap-3">
            <DateTimePicker data-testid="cl-start" withTime={!form.all_day} value={form.start_time} onChange={(v) => setForm((f) => ({ ...f, start_time: v }))} />
            <DateTimePicker data-testid="cl-end" withTime={!form.all_day} value={form.end_time} onChange={(v) => setForm((f) => ({ ...f, end_time: v }))} />
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-300">
            <input type="checkbox" checked={form.all_day} onChange={(e) => setForm((f) => ({ ...f, all_day: e.target.checked }))} data-testid="cl-allday" />
            All-day lesson
          </label>
          <div className="grid grid-cols-2 gap-3">
            <input className="pp-input" data-testid="cl-price" type="number" step={1} min={0} value={form.price} onChange={set("price")} placeholder="Price" />
            <input className="pp-input" data-testid="cl-max" type="number" min={1} max={20} value={form.max_players} onChange={set("max_players")} placeholder="Max" />
          </div>
          <div className="flex gap-2" data-testid="cl-visibility">
            <button type="button" data-testid="cl-vis-public" onClick={() => setForm((f) => ({ ...f, visibility: "public" }))}
              className={`pp-chip flex-1 ${form.visibility === "public" ? "pp-chip-active" : ""}`}>Public</button>
            <button type="button" data-testid="cl-vis-private" onClick={() => setForm((f) => ({ ...f, visibility: "private" }))}
              className={`pp-chip flex-1 ${form.visibility === "private" ? "pp-chip-active" : ""}`}>Private (invite/QR)</button>
          </div>
          <button data-testid="cl-submit" className="pp-btn-primary px-5 py-3 rounded-xl">{t("create")}</button>
        </form>

        <div className="pp-card p-6">
          <h3 className="font-heading text-xl font-bold">My Lessons</h3>
          <div className="mt-3 space-y-2 max-h-[500px] overflow-auto" data-testid="coach-lessons">
            {lessons.map((l) => (
              <div key={l.id} className="pp-card p-3 text-sm">
                <div className="font-bold">{l.title}</div>
                <div className="text-xs text-zinc-500">{new Date(l.start_time).toLocaleString()} · {l.price} {l.currency} · {l.registrations?.length || 0} reg.</div>
              </div>
            ))}
            {lessons.length === 0 && <div className="text-zinc-500 text-sm">No lessons yet.</div>}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Kpi({ label, value }) {
  return <div className="pp-card p-4"><div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div><div className="font-heading text-2xl font-bold mt-1 text-[#00E676]">{value}</div></div>;
}
